import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

import {
  generateKeypair,
  encryptPrivateKey,
  decryptPrivateKey,
  encodePublicKey,
} from '../crypto/encryption';
import { deriveKeys, hashAuthKey, generateKDFParams } from '../crypto/kdf';
import type { KDFParams, User } from '../types';
import {
  LOGIN_MUTATION,
  SIGNUP_MUTATION,
  LOGOUT_MUTATION,
  ME_QUERY,
  GET_KDF_PARAMS,
  REQUEST_SIGNUP_EMAIL_CODE_MUTATION,
  SEND_DEVICE_VERIFICATION_CODE_MUTATION,
  VERIFY_DEVICE_CODE_MUTATION,
} from '../services/graphql';
import {
  apolloClient,
  setDeviceRevokedCallback,
  clearDeviceRevokedCallback,
  setSessionExpiredCallback,
  clearSessionExpiredCallback,
} from '../services/apollo';
import { getOrCreateDeviceId } from '../utils/deviceId';

interface RequestEmailCodeResult {
  success: boolean;
  message?: string;
  retryAfter?: number;
}

interface LoginResult {
  success: boolean;
  requiresDeviceVerification?: boolean;
  challengeId?: string;
  kdfParams?: KDFParams;
}

interface SendDeviceCodeResult {
  success: boolean;
  message?: string;
  retryAfter?: number;
}

interface VerifyDeviceCodeResult {
  success: boolean;
  message?: string;
  remainingAttempts?: number;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  needsUnlock: boolean;
  vaultKey: Uint8Array | null;
  privateKey: Uint8Array | null;
  /** Message to show when device was revoked (triggers auto-logout) */
  deviceRevokedMessage: string | null;
  /** Message to show when session expired (refresh token invalid) */
  sessionExpiredMessage: string | null;
  /** Clear the device revoked message after displaying */
  clearDeviceRevokedMessage: () => void;
  /** Clear the session expired message after displaying */
  clearSessionExpiredMessage: () => void;
  login: (email: string, masterPassword: string, turnstileToken?: string) => Promise<LoginResult>;
  sendDeviceVerificationCode: (challengeId: string) => Promise<SendDeviceCodeResult>;
  verifyDeviceCode: (challengeId: string, code: string, trustDevice: boolean, vaultKeyForSession: Uint8Array) => Promise<VerifyDeviceCodeResult>;
  requestSignupEmailCode: (email: string, turnstileToken: string) => Promise<RequestEmailCodeResult>;
  signup: (
    email: string,
    masterPassword: string,
    emailCode: string | undefined,
    turnstileToken: string,
    workspaceInviteToken?: string
  ) => Promise<{ needsWorkspaceSetup: boolean }>;
  unlock: (masterPassword: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [vaultKey, setVaultKey] = useState<Uint8Array | null>(null);
  const [privateKey, setPrivateKey] = useState<Uint8Array | null>(null);
  const [deviceRevokedMessage, setDeviceRevokedMessage] = useState<string | null>(null);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState<string | null>(null);

  // GraphQL operations
  const [loginMutation] = useMutation(LOGIN_MUTATION);
  const [requestEmailCodeMutation] = useMutation(REQUEST_SIGNUP_EMAIL_CODE_MUTATION);
  const [signupMutation] = useMutation(SIGNUP_MUTATION);
  const [logoutMutation] = useMutation(LOGOUT_MUTATION);
  const [sendDeviceCodeMutation] = useMutation(SEND_DEVICE_VERIFICATION_CODE_MUTATION);
  const [verifyDeviceCodeMutation] = useMutation(VERIFY_DEVICE_CODE_MUTATION);
  const [getKDFParams] = useLazyQuery(GET_KDF_PARAMS);

  // User is authenticated but vault is locked (needs master password to derive vaultKey)
  const needsUnlock = !!user && !vaultKey;

  // Handle device revoked callback from Apollo error link
  useEffect(() => {
    const handleDeviceRevoked = () => {
      // Clear auth state
      setUser(null);
      setVaultKey(null);
      setPrivateKey(null);
      setDeviceRevokedMessage('This device has been revoked. Please sign in again to verify this device.');
      // Clear Apollo cache
      apolloClient.clearStore();
    };

    const handleSessionExpired = () => {
      // Clear auth state
      setUser(null);
      setVaultKey(null);
      setPrivateKey(null);
      setSessionExpiredMessage('Your session has expired. Please sign in again.');
      // Clear Apollo cache
      apolloClient.clearStore();
    };

    setDeviceRevokedCallback(handleDeviceRevoked);
    setSessionExpiredCallback(handleSessionExpired);

    return () => {
      clearDeviceRevokedCallback();
      clearSessionExpiredCallback();
    };
  }, []);

  const clearDeviceRevokedMessage = useCallback(() => {
    setDeviceRevokedMessage(null);
  }, []);

  const clearSessionExpiredMessage = useCallback(() => {
    setSessionExpiredMessage(null);
  }, []);

  // Check if user is already authenticated
  const { data: meData, loading: meLoading } = useQuery(ME_QUERY, {
    fetchPolicy: 'network-only',
    onError: () => {
      // Not authenticated
      setIsLoading(false);
    },
  });

  useEffect(() => {
    if (!meLoading) {
      if (meData?.me) {
        setUser(meData.me);
      }
      setIsLoading(false);
    }
  }, [meData, meLoading]);

  const login = useCallback(async (email: string, masterPassword: string, turnstileToken?: string): Promise<LoginResult> => {
    // Get KDF params for this user
    const { data: kdfData } = await getKDFParams({ variables: { email } });
    const kdfParams: KDFParams = kdfData?.getKDFParams;

    if (!kdfParams) {
      throw new Error('Failed to get KDF parameters');
    }

    // Derive keys from master password
    const keys = await deriveKeys(masterPassword, kdfParams);

    // Hash auth key for authentication
    const authKeyHash = await hashAuthKey(keys.authKey);

    // Get or create device ID
    const deviceId = getOrCreateDeviceId();

    // Build input with optional turnstile token and device ID
    const input: { email: string; authKeyHash: string; turnstileToken?: string; deviceId?: string } = {
      email,
      authKeyHash,
      deviceId,
    };

    if (turnstileToken) {
      input.turnstileToken = turnstileToken;
    }

    // Perform login
    const { data } = await loginMutation({
      variables: { input },
    });

    // Check if device verification is required
    if (data?.login?.requiresDeviceVerification) {
      // Store the vault key temporarily for use after device verification
      // We'll pass it via the verify function
      return {
        success: false,
        requiresDeviceVerification: true,
        challengeId: data.login.challengeId,
        kdfParams: data.login.kdfParams,
      };
    }

    if (!data?.login?.success) {
      throw new Error(data?.login?.message || 'Login failed');
    }

    const loggedInUser = data.login.user as User;
    setUser(loggedInUser);
    setVaultKey(keys.vaultKey);

    // Store tokens if returned (for development)
    if (data.login._accessToken) {
      localStorage.setItem('accessToken', data.login._accessToken);
    }
    if (data.login._refreshToken) {
      localStorage.setItem('refreshToken', data.login._refreshToken);
    }

    // Decrypt private key from user data
    const decryptedPrivKey = decryptPrivateKey(
      loggedInUser.encryptedPrivateKey,
      loggedInUser.privateKeyNonce,
      keys.vaultKey
    );
    if (decryptedPrivKey) {
      setPrivateKey(decryptedPrivKey);
    }

    return { success: true };
  }, [getKDFParams, loginMutation]);

  // Send device verification code
  const sendDeviceVerificationCode = useCallback(async (challengeId: string): Promise<SendDeviceCodeResult> => {
    const { data } = await sendDeviceCodeMutation({
      variables: { challengeId },
    });

    if (!data?.sendDeviceVerificationCode) {
      throw new Error('Failed to send verification code');
    }

    return {
      success: data.sendDeviceVerificationCode.success,
      message: data.sendDeviceVerificationCode.message,
      retryAfter: data.sendDeviceVerificationCode.retryAfter,
    };
  }, [sendDeviceCodeMutation]);

  // Verify device code and complete login
  const verifyDeviceCode = useCallback(async (
    challengeId: string,
    code: string,
    trustDevice: boolean,
    vaultKeyForSession: Uint8Array
  ): Promise<VerifyDeviceCodeResult> => {
    const { data } = await verifyDeviceCodeMutation({
      variables: {
        input: {
          challengeId,
          code,
          trustDevice,
        },
      },
    });

    if (!data?.verifyDeviceCode?.success) {
      return {
        success: false,
        message: data?.verifyDeviceCode?.message || 'Verification failed',
        remainingAttempts: data?.verifyDeviceCode?.remainingAttempts,
      };
    }

    // Verification successful - set up session
    const loggedInUser = data.verifyDeviceCode.user as User;
    setUser(loggedInUser);
    setVaultKey(vaultKeyForSession);

    // Store tokens if returned (for development)
    if (data.verifyDeviceCode._accessToken) {
      localStorage.setItem('accessToken', data.verifyDeviceCode._accessToken);
    }
    if (data.verifyDeviceCode._refreshToken) {
      localStorage.setItem('refreshToken', data.verifyDeviceCode._refreshToken);
    }

    // Decrypt private key from user data
    const decryptedPrivKey = decryptPrivateKey(
      loggedInUser.encryptedPrivateKey,
      loggedInUser.privateKeyNonce,
      vaultKeyForSession
    );
    if (decryptedPrivKey) {
      setPrivateKey(decryptedPrivKey);
    }

    return { success: true };
  }, [verifyDeviceCodeMutation]);

  // Unlock vault with master password (used after page refresh when session exists but vaultKey is lost)
  const unlock = useCallback(async (masterPassword: string) => {
    if (!user) {
      throw new Error('No user session. Please log in.');
    }

    // Get KDF params for this user
    const { data: kdfData } = await getKDFParams({ variables: { email: user.email } });
    const kdfParams: KDFParams = kdfData?.getKDFParams;

    if (!kdfParams) {
      throw new Error('Failed to get KDF parameters');
    }

    // Derive keys from master password
    const keys = await deriveKeys(masterPassword, kdfParams);

    // Verify the password by hashing and checking against server
    const authKeyHash = await hashAuthKey(keys.authKey);

    // Perform a "silent" login to verify the password is correct and get fresh user data
    const { data } = await loginMutation({
      variables: {
        input: {
          email: user.email,
          authKeyHash,
        },
      },
    });

    if (!data?.login?.success) {
      throw new Error('Invalid master password');
    }

    // Password is correct, set the vault key
    setVaultKey(keys.vaultKey);

    // Update user with fresh data (including encrypted private key)
    const freshUser = data.login.user as User;
    setUser(freshUser);

    // Decrypt private key from user data
    const decryptedPrivKey = decryptPrivateKey(
      freshUser.encryptedPrivateKey,
      freshUser.privateKeyNonce,
      keys.vaultKey
    );
    if (decryptedPrivKey) {
      setPrivateKey(decryptedPrivKey);
    }
  }, [user, getKDFParams, loginMutation]);

  // Request signup email verification code
  const requestSignupEmailCode = useCallback(async (email: string, turnstileToken: string): Promise<RequestEmailCodeResult> => {
    const { data } = await requestEmailCodeMutation({
      variables: { email, turnstileToken },
    });

    if (!data?.requestSignupEmailCode) {
      throw new Error('Failed to request email code');
    }

    return {
      success: data.requestSignupEmailCode.success,
      message: data.requestSignupEmailCode.message,
      retryAfter: data.requestSignupEmailCode.retryAfter,
    };
  }, [requestEmailCodeMutation]);

  const signup = useCallback(async (
    email: string,
    masterPassword: string,
    emailCode: string | undefined,
    turnstileToken: string,
    workspaceInviteToken?: string
  ): Promise<{ needsWorkspaceSetup: boolean }> => {
    // Generate KDF params
    const kdfParams = generateKDFParams();

    // Derive keys from master password
    const keys = await deriveKeys(masterPassword, kdfParams);

    // Hash auth key for authentication
    const authKeyHash = await hashAuthKey(keys.authKey);

    // Generate keypair for sharing
    const keypair = generateKeypair();

    // Encrypt private key with vault key
    const { encryptedPrivateKey, privateKeyNonce } = encryptPrivateKey(
      keypair.privateKey,
      keys.vaultKey
    );

    // Get device ID for trusted device registration
    const deviceId = getOrCreateDeviceId();

    // Build input - include workspace invite token if provided
    const input: Record<string, unknown> = {
      email,
      authKeyHash,
      publicKey: encodePublicKey(keypair.publicKey),
      encryptedPrivateKey,
      privateKeyNonce,
      kdfParams,
      turnstileToken,
      deviceId,
    };

    if (emailCode) {
      input.emailCode = emailCode;
    }

    if (workspaceInviteToken) {
      input.workspaceInviteToken = workspaceInviteToken;
    }

    // Perform signup with Turnstile token and email code
    const { data } = await signupMutation({
      variables: { input },
    });

    if (!data?.signup?.success) {
      throw new Error(data?.signup?.message || 'Signup failed');
    }

    const newUser = data.signup.user;
    setUser(newUser);
    setVaultKey(keys.vaultKey);
    setPrivateKey(keypair.privateKey);

    // Store tokens if returned (for development)
    if (data.signup._accessToken) {
      localStorage.setItem('accessToken', data.signup._accessToken);
    }
    if (data.signup._refreshToken) {
      localStorage.setItem('refreshToken', data.signup._refreshToken);
    }

    // Return whether user needs to create a workspace
    return { needsWorkspaceSetup: newUser.needsWorkspaceSetup ?? !workspaceInviteToken };
  }, [signupMutation]);

  const logout = useCallback(async () => {
    try {
      await logoutMutation();
    } catch {
      // Ignore logout errors
    }

    // Clear local state
    setUser(null);
    setVaultKey(null);
    setPrivateKey(null);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');

    // Clear Apollo cache
    await apolloClient.clearStore();
  }, [logoutMutation]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        needsUnlock,
        vaultKey,
        privateKey,
        deviceRevokedMessage,
        sessionExpiredMessage,
        clearDeviceRevokedMessage,
        clearSessionExpiredMessage,
        login,
        sendDeviceVerificationCode,
        verifyDeviceCode,
        requestSignupEmailCode,
        signup,
        unlock,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
